import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Lock, CheckCircle2, RotateCcw, Loader2, Banknote } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SETTLEMENT_STYLE = {
  pending: 'bg-slate-100 text-slate-600',
  held: 'bg-amber-100 text-amber-700',
  released: 'bg-emerald-100 text-emerald-700',
  refunded: 'bg-red-100 text-red-700',
};

// `escrow_status` is an internal legacy DB field (numeric/state machine). The
// label shown to admins is the compliant marketplace-software terminology.
const SETTLEMENT_LABEL = {
  pending: 'awaiting verification',
  held: 'awaiting verification',
  released: 'settled',
  refunded: 'refunded',
};

export const AdminRevenuePanel = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load revenue data'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const release = async (row) => {
    if (!row.booking_id) return;
    setBusyId(row.id);
    try {
      await axios.post(`${API}/bookings/${row.booking_id}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Settlement released to clinician');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not release settlement'); }
    finally { setBusyId(null); }
  };
  const refund = async (row) => {
    if (!row.booking_id) return;
    setBusyId(row.id);
    try {
      await axios.post(`${API}/admin/bookings/${row.booking_id}/refund`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Booking marked refunded');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not refund'); }
    finally { setBusyId(null); }
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>;
  if (!data) return null;
  const s = data.summary;

  const cards = [
    { label: 'Gross volume', value: money(s.gross_volume), icon: TrendingUp, color: 'text-blue-600' },
    { label: `Platform revenue (${Math.round((s.platform_fee_pct ?? 0.12) * 100)}%)`, value: money(s.platform_revenue), icon: DollarSign, color: 'text-emerald-600' },
    { label: 'Awaiting verification', value: money(s.in_escrow), icon: Lock, color: 'text-amber-600' },
    { label: 'Settled', value: money(s.released), icon: CheckCircle2, color: 'text-teal-600 dark:text-teal-300' },
    { label: 'Refunded', value: money(s.refunded), icon: RotateCcw, color: 'text-red-600' },
  ];

  return (
    <div data-testid="admin-revenue-panel" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-revenue-summary">
        {cards.map((c) => (
          <div key={c.label} className="bg-white dark:bg-cn-ink-800 rounded-xl p-4 border border-slate-200 dark:border-cn-ink-700">
            <c.icon className={`w-5 h-5 ${c.color}`} />
            <p className={`text-xl font-bold mt-2 font-display ${c.color}`} data-testid={`revenue-${c.label.split(' ')[0].toLowerCase()}`}>{c.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-cn-ink-800 rounded-2xl border border-slate-200 dark:border-cn-ink-700 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-cn-ink-700">
          <h3 className="font-display font-bold text-slate-900 dark:text-white">Transactions ({s.transaction_count})</h3>
        </div>
        {data.transactions.length === 0 ? (
          <div className="py-10 text-center text-slate-500 dark:text-slate-400" data-testid="admin-revenue-empty">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-cn-ink-700/50 text-left text-xs text-slate-500 dark:text-slate-400 uppercase">
                <tr>
                  <th className="px-4 py-2">Patient → Clinician</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Fee</th>
                  <th className="px-4 py-2">Net</th>
                  <th className="px-4 py-2">Payment</th>
                  <th className="px-4 py-2">Settlement</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t) => (
                  <tr key={t.id} data-testid={`admin-txn-${t.id}`} className="border-t border-slate-100 dark:border-cn-ink-700/50">
                    <td className="px-4 py-3">
                      <div className="text-slate-900 dark:text-white">{t.patient_name} → {t.clinician_name}</div>
                      <div className="text-xs text-slate-400">{t.service_type || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white">{money(t.amount)}</td>
                    <td className="px-4 py-3 text-slate-500">{money(t.platform_fee)}</td>
                    <td className="px-4 py-3 text-slate-500">{money(t.clinician_earnings)}</td>
                    <td className="px-4 py-3"><span className="text-xs">{t.payment_status || '—'}</span></td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${SETTLEMENT_STYLE[t.escrow_status] || 'bg-slate-100 text-slate-600'}`}>{SETTLEMENT_LABEL[t.escrow_status] || t.escrow_status}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {t.booking_id && t.escrow_status === 'held' && (
                        <button onClick={() => release(t)} disabled={busyId === t.id} data-testid={`admin-release-${t.id}`} className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg disabled:opacity-50 mr-1">Release</button>
                      )}
                      {t.booking_id && t.escrow_status === 'held' && (
                        <button onClick={() => refund(t)} disabled={busyId === t.id} data-testid={`admin-refund-${t.id}`} className="text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg disabled:opacity-50">Refund</button>
                      )}
                      {busyId === t.id && <Loader2 className="w-4 h-4 animate-spin inline text-cn-primary" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.payouts.length > 0 && (
        <div className="bg-white dark:bg-cn-ink-800 rounded-2xl border border-slate-200 dark:border-cn-ink-700 p-4" data-testid="admin-revenue-payouts">
          <h3 className="font-display font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2"><Banknote className="w-5 h-5 text-cn-primary" /> Clinician payouts owed</h3>
          <div className="space-y-2">
            {data.payouts.map((p) => (
              <div key={p.clinician_id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-cn-ink-700/50">
                <div><span className="font-medium text-slate-900 dark:text-white">{p.clinician_name}</span> <span className="text-xs text-slate-400">· {p.bookings} released</span></div>
                <span className="font-bold text-cn-primary">{money(p.owed)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRevenuePanel;
